import { Home, CalendarDays, Building2, PhoneCall, Globe } from "lucide-react";

/**
 * Branded source logo used in dashboard tables (Burimi column).
 * Combines a small coloured chip with a recognisable brand glyph
 * (monogram for OTAs — this IS what these companies use as their
 * app icon) plus the human-readable brand name.
 *
 * Supported: booking, airbnb, expedia, hotels_com / hotels, agoda,
 * vrbo, google_hotels, direct, booking_engine, walk_in, phone, ical.
 */
const BRAND = {
    booking:        { name: "Booking.com",  bg: "#003580", fg: "#fff",     glyph: "B." },
    "booking.com":  { name: "Booking.com",  bg: "#003580", fg: "#fff",     glyph: "B." },
    airbnb:         { name: "Airbnb",       bg: "#FF385C", fg: "#fff",     svg: "airbnb" },
    expedia:        { name: "Expedia",      bg: "#FFC72C", fg: "#00355F",  glyph: "E" },
    hotels:         { name: "Hotels.com",   bg: "#D32F2F", fg: "#fff",     glyph: "H" },
    "hotels.com":   { name: "Hotels.com",   bg: "#D32F2F", fg: "#fff",     glyph: "H" },
    agoda:          { name: "Agoda",        bg: "#5C2D91", fg: "#fff",     glyph: "a" },
    vrbo:           { name: "Vrbo",         bg: "#245ABC", fg: "#fff",     glyph: "V" },
    google_hotels:  { name: "Google Hotels",bg: "#4285F4", fg: "#fff",     glyph: "G" },
    ical:           { name: "iCal",         bg: "#64748b", fg: "#fff",     icon: CalendarDays },
    direct:         { name: "Direct",       bg: "#0F172A", fg: "#fff",     icon: Home },
    booking_engine: { name: "Direct",       bg: "#0F172A", fg: "#fff",     icon: Home },
    walk_in:        { name: "Walk-in",      bg: "#0F172A", fg: "#fff",     icon: Building2 },
    phone:          { name: "Phone",        bg: "#0F172A", fg: "#fff",     icon: PhoneCall },
};

// Simplified Airbnb Bélo — recognisable pin-heart mark (public brand)
function AirbnbGlyph({ size = 10 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="currentColor" aria-hidden>
            <path d="M16 3c-2.4 0-4.4 1.9-4.4 4.4 0 1 .3 1.9.9 2.8C7.9 15.7 3.5 21 3.5 24.7c0 2.6 2 4.8 4.8 4.8 1.6 0 3.1-.7 4.3-1.9.4-.4.8-.8 1.3-1.4l2.1-2.5 2.1 2.5c.5.6.9 1 1.3 1.4 1.2 1.2 2.7 1.9 4.3 1.9 2.8 0 4.8-2.2 4.8-4.8 0-3.7-4.4-9-9-14.5.6-.9.9-1.8.9-2.8C20.4 4.9 18.4 3 16 3zm0 2.6c1.2 0 2 .8 2 2s-.8 2-2 2-2-.8-2-2 .8-2 2-2z"/>
        </svg>
    );
}

export default function SourceLogo({ source, showName = true, size = "sm" }) {
    const key = String(source || "direct").toLowerCase();
    const brand = BRAND[key] || { name: source || "Direct", bg: "#e2e8f0", fg: "#334155", icon: Globe };
    const chipCls = size === "xs"
        ? "h-[14px] w-[14px] text-[8px]"
        : "h-[16px] w-[16px] text-[9px]";
    const Icon = brand.icon;
    return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-testid={`source-${key}`}>
            <span
                className={`inline-flex shrink-0 items-center justify-center rounded-[3px] font-black leading-none ${chipCls}`}
                style={{ background: brand.bg, color: brand.fg, letterSpacing: brand.glyph === "B." ? "-0.05em" : undefined }}
                aria-label={brand.name}
            >
                {brand.svg === "airbnb" && <AirbnbGlyph size={size === "xs" ? 9 : 10} />}
                {brand.glyph && !brand.svg && <span>{brand.glyph}</span>}
                {Icon && <Icon size={size === "xs" ? 9 : 10} strokeWidth={2.5} />}
            </span>
            {showName && (
                <span className="truncate text-[12px] text-foreground/90">{brand.name}</span>
            )}
        </span>
    );
}
